/**
 * B40 — DEPTH THAT RESPONDS.
 *
 * The board is a flat overlay painted on a moving picture, and the one thing a
 * flat overlay never has is air behind it. A couple of pixels of drift against
 * the stream as the pointer moves is what supplies it: the board reads as a
 * pane held a little in front of the green rather than as graphics stuck to the
 * glass.
 *
 * WHY IT IS SAFE HERE WHERE THE 3D SEAT WAS NOT. The tilt was rejected because
 * it moved the outer rows 10-20 px off the positions `boardGeometry` computes
 * against the video picture. This is capped at THREE, which is inside the
 * bleeds the geometry already reserves around the board (7 px on the left for
 * the lightning seal, 9 px on the right for the chip pile), and it is applied as
 * one composited `translate3d` on the frame — the layout underneath never moves,
 * so nothing the geometry asserts can drift.
 *
 * Pure, so the arithmetic — and in particular the CAP, which is the whole safety
 * argument — can be asserted without a pointer.
 */

/**
 * The furthest the board ever drifts from its own position, px.
 *
 * It is the SMALLER of the two bleeds `boardGeometry` reserves around the tile
 * field — 7 px on the left for the lightning seal, 9 px on the right for the
 * chip pile — because a drift inside the smaller one is inside both. That is not
 * a tuning number, it is the safety argument: the board may move as far as the
 * geometry has already promised nothing else is, and not one px further.
 *
 * It started at 3, which was honest but below the threshold a player feels
 * (owner: "not doing much when moving the cursor around"). The ceiling the
 * argument actually allows is 7, and a guard test keys it to
 * `BIRDIE_BOARD_SPEC.tileBleed` rather than to this literal.
 */
export const PARALLAX_CAP_PX = 7

/**
 * How far the board travels toward its target each frame, as a fraction.
 *
 * INERTIA is what makes the drift felt rather than measured: a board that tracks
 * the pointer rigidly reads as a cursor attachment, and one that eases toward it
 * reads as something with weight sitting behind glass. At 0.14 a full-width
 * sweep settles in about a fifth of a second, which is enough to feel and short
 * enough never to be waited on.
 */
export const PARALLAX_EASE = 0.14

/** Below this the board is at its target, the loop stops and nothing is scheduled. */
export const PARALLAX_SETTLED_PX = 0.02


export interface ParallaxOffset {
  x: number
  y: number
}

const NONE: ParallaxOffset = { x: 0, y: 0 }

/**
 * At most two decimals: enough resolution for a 3-px range, and it keeps the
 * string written into the custom property short. `+ 0` normalises a negative
 * zero, which is a real value in JS and would be written as `-0px`.
 */
function quantise(value: number): number {
  return Math.round(value * 100) / 100 + 0
}

/**
 * The board's drift for a pointer at `clientX/clientY` in a `width x height`
 * viewport, in px.
 *
 * INVERTED on both axes: the board moves AWAY from the pointer, which is the
 * direction a near plane moves when the head does. The magnitude is clamped to
 * `PARALLAX_CAP_PX` whatever the caller passes, so a bad viewport reading — a
 * zero, a negative, a pointer outside the window — can never push the board
 * further than the cap.
 */
export function parallaxOffset(
  clientX: number,
  clientY: number,
  width: number,
  height: number,
  capPx: number = PARALLAX_CAP_PX
): ParallaxOffset {
  if (!(width > 0) || !(height > 0)) return NONE
  if (!isFinite(clientX) || !isFinite(clientY)) return NONE
  const cap = Math.min(Math.abs(capPx), PARALLAX_CAP_PX)
  const fromCentreX = clamp(clientX / width - 0.5, -0.5, 0.5) * 2
  const fromCentreY = clamp(clientY / height - 0.5, -0.5, 0.5) * 2
  return { x: quantise(-fromCentreX * cap), y: quantise(-fromCentreY * cap) }
}

/**
 * One frame of the ease, and the flag that stops the loop.
 *
 * Pure, and separate from the component, because "it settles and then schedules
 * nothing" is the whole perf claim: a lerp that never quite arrives would leave
 * a rAF running over live video for the length of the round.
 */
export function approach(
  current: number,
  target: number,
  ease: number = PARALLAX_EASE
): { value: number; settled: boolean } {
  const next = current + (target - current) * ease
  if (Math.abs(target - next) < PARALLAX_SETTLED_PX) return { value: target, settled: true }
  // FULL PRECISION on the way. Rounding here once cost the loop its arrival: near
  // the target a frame's progress is smaller than the rounding step, the value
  // stopped changing, the gap never closed and the rAF re-armed for ever. The
  // rounding belongs at the WRITE, which is what `pxValue` is for.
  return { value: next, settled: false }
}

/** The value as it is written into the custom property — rounded, and in px. */
export function pxValue(value: number): string {
  return `${quantise(value)}px`
}

function clamp(value: number, low: number, high: number): number {
  if (value < low) return low
  if (value > high) return high
  return value
}
